import { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send, RotateCcw } from 'lucide-react';
import { streamAssistant } from './streamAssistant';
import RichText from './RichText';

const API = process.env.REACT_APP_BACKEND_URL;
const KEY = 'ff_assistant_session';
const SUGGESTIONS = ['Is 9780007202263 worth buying?', 'How does the Batch Checker work?', 'What is the CEX cash equivalent?'];

export default function AssistantWidget() {
  const [open, setOpenState] = useState(() => sessionStorage.getItem('ff_assistant_open') === '1');
  const setOpen = (v) => { sessionStorage.setItem('ff_assistant_open', v ? '1' : '0'); setOpenState(v); };
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(KEY));
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [left, setLeft] = useState(null);
  const endRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/api/assistant/usage`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setLeft(d.unlimited ? null : d.chat_left));
  }, [open]);

  useEffect(() => {
    if (!open || !sessionId || messages.length) return;
    fetch(`${API}/api/assistant/history/${sessionId}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { messages: [] }))
      .then((d) => setMessages(d.messages || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sessionId]);

  useEffect(() => endRef.current?.scrollIntoView({ behavior: 'smooth' }), [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput('');
    setSending(true);
    setMessages((m) => [...m, { role: 'user', content: msg }, { role: 'assistant', content: '' }]);
    const append = (chunk) => setMessages((m) => {
      const copy = [...m];
      copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + chunk };
      return copy;
    });
    await streamAssistant(msg, sessionId, {
      onSession: (id, remainingToday) => { localStorage.setItem(KEY, id); setSessionId(id); setLeft(remainingToday ?? null); },
      onDelta: append,
      onError: (e, status) => { append(e); if (status === 429) setLeft(0); },
    });
    setSending(false);
  };

  const newChat = () => { localStorage.removeItem(KEY); setSessionId(null); setMessages([]); };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="fixed bottom-5 right-5 z-50 flex items-center gap-2 px-4 py-3 rounded-full font-bold text-sm shadow-lg hover:scale-105 transition-transform" style={{ background: 'linear-gradient(90deg,#00D26A,#FFD700)', color: '#050505' }} data-testid="assistant-open-btn">
        <MessageCircle className="w-5 h-5" /> Flip Buddy
      </button>
    );
  }
  return (
    <div className="fixed bottom-3 inset-x-3 sm:inset-x-auto sm:bottom-5 sm:right-5 z-50 sm:w-full max-w-sm h-[70vh] max-h-[560px] flex flex-col bg-zinc-950 border border-zinc-800 rounded-2xl shadow-2xl" data-testid="assistant-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
        <div>
          <div className="font-bold text-white text-sm">Flip Buddy</div>
          <div className="text-[11px] text-zinc-500" data-testid="assistant-allowance">
            AI helper{left !== null ? ` · ${left} message${left === 1 ? '' : 's'} left today` : ' · checks your price library'}
          </div>
        </div>
        <div className="flex gap-1">
          <button onClick={newChat} className="p-2 rounded-full hover:bg-zinc-800 text-zinc-400" title="New chat" data-testid="assistant-new-chat-btn"><RotateCcw className="w-4 h-4" /></button>
          <button onClick={() => setOpen(false)} className="p-2 rounded-full hover:bg-zinc-800 text-zinc-400" data-testid="assistant-close-btn"><X className="w-4 h-4" /></button>
        </div>
      </div>
      <MessageList messages={messages} sending={sending} onSuggest={send} endRef={endRef} />
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 p-3 border-t border-zinc-800">
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about an item or barcode…" className="flex-1 bg-black border border-zinc-800 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-[#00D26A]" data-testid="assistant-input" />
        <button type="submit" disabled={sending || !input.trim() || left === 0} className="p-2.5 rounded-full disabled:opacity-40" style={{ background: '#00D26A', color: '#050505' }} data-testid="assistant-send-btn"><Send className="w-4 h-4" /></button>
      </form>
    </div>
  );
}

function MessageList({ messages, sending, onSuggest, endRef }) {
  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-3 text-sm" data-testid="assistant-messages">
      {messages.length === 0 && (
        <div className="space-y-2">
          <p className="text-zinc-400">Ask me about prices, buyers or how the app works.</p>
          {SUGGESTIONS.map((s, i) => (
            <button key={s} onClick={() => onSuggest(s)} className="block w-full text-left px-3 py-2 rounded-lg border border-zinc-800 text-zinc-300 hover:border-[#00D26A] transition-colors" data-testid={`assistant-suggestion-${i}`}>{s}</button>
          ))}
        </div>
      )}
      {messages.map((m, i) => (
        <div key={i} className={`whitespace-pre-wrap rounded-2xl px-3 py-2 max-w-[90%] ${m.role === 'user' ? 'ml-auto bg-[#00D26A] text-black' : 'bg-zinc-900 text-zinc-200'}`} data-testid={`assistant-msg-${m.role}-${i}`}>
          {m.content ? <RichText text={m.content} testid={`assistant-msg-${i}`} /> : (sending ? '…' : '')}
        </div>
      ))}
      <div ref={endRef} />
    </div>
  );
}
